import React, { useEffect, useRef, useState } from 'react';
import { Icons, IconsVariants, Spinner, SpinnerVariants } from '../../../UI';
import { DialogComponent } from '../../../components/DialogComponent/DialogComponent.tsx';
import { ImageLightbox } from '../../../components/ImageLightbox/ImageLightbox';
import { useUploadUserImageMutation } from '../../../apis';
import { imageSrc } from '../../../utils/imageUrl';

interface ProfileImageProps {
  imageUrl?: string;
  userId?: string;
  currentUserId: string | null;
}

export const ProfileImage: React.FC<ProfileImageProps> = ({ imageUrl, userId, currentUserId }) => {

  const [uploadUserImage, { isLoading }] = useUploadUserImageMutation();

  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imgKey, setImgKey] = useState<string | undefined>(imageUrl);

  // Sync when the prop changes (viewing a different profile, or fresh data after refetch).
  useEffect(() => {
    setImgKey(imageUrl);
  }, [imageUrl]);

  const changeImgHandler = () => {
    inputRef.current?.click();
  };

  useEffect(() => {
    if (!file) return;

    (async () => {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('imageUrlFor', 'profile');

      const result = await uploadUserImage(formData);

      if ('error' in result && result.error) {
        const message = 'message' in result.error ? result.error.message : 'Unknown error';
        setErrorMessage(message as string);
        setIsOpen(true);
      } else if ('data' in result) {
        setImgKey(result.data.imgUrl);
      }

    })();
  }, [file, uploadUserImage]);

  const displaySrc = imgKey ? imageSrc(imgKey, 'md') : '/profile-man.png';

  if (userId !== currentUserId) {
    // Other people's profile: clicking their avatar opens a larger view
    // (only when they actually have one — not for the default placeholder).
    const hasAvatar = !!imgKey;
    const avatarImg = (
      <img
        className="rounded-full absolute inset-0 object-cover w-full h-full border-app-sm border-black"
        src={displaySrc}
        alt=''
      />
    );
    return (
      <>
        <div className="absolute h-32 w-32 bottom-[-15%] left-4 rounded-full ">
          {hasAvatar ? (
            <button
              type="button"
              aria-label="View profile picture"
              onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }}
              className="absolute inset-0 rounded-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary"
            >
              {avatarImg}
            </button>
          ) : avatarImg}
        </div>
        {hasAvatar && (
          <ImageLightbox
            src={imageSrc(imgKey, 'orig')}
            alt="Profile picture"
            isOpen={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <DialogComponent
        isOpen={isOpen}
        closeDialog={() => setIsOpen(false)}
        content={errorMessage ?? ''}
      />
      <div className="group absolute h-32 w-32 bottom-[-15%] left-4 rounded-full hover:bg-app-card  " onClick={changeImgHandler}>
        {
          isLoading
            ? <div className='flex h-full justify-center items-center'>
              <Spinner variant={SpinnerVariants.SMALL_CIRCLE} />
            </div>
            : <>
              <img
                className="group-hover:opacity-40 rounded-full absolute inset-0 object-cover w-full h-full border-app-sm hover:opacity-40 border-black cursor-pointer"
                src={displaySrc}
                alt=''
              />
              <input
                type='file'
                ref={inputRef}
                onChange={ev => setFile(ev.target.files?.[0] || null)}
                className=' invisible'
              />

              <div className={`group-hover:visible absolute top-2 right-2 h-7 w-7 invisible cursor-pointer`}>
                <Icons variant={IconsVariants.EDIT} classes={`fill-white`} />
              </div>
            </>
        }
      </div>
    </>
  );
};
